import { useEffect } from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectCurrentHotelName } from '../../accessors/index'
import { getInsights } from '../../actions/insights'
import ChartBox from '../../components/ReusableComponents/Charts/Inslghts/ChartBox/index'
import MultiBarCard from '../../components/ReusableComponents/Charts/Inslghts/MultiBar/index'
import PieCard from '../../components/ReusableComponents/Charts/Inslghts/Pie/index'
import ComissionSlider from '../../components/ReusableComponents/CommissionSlider/index'
import CustomHeader from '../../components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from '../../components/ReusableComponents/LoadingIndicator'
import { OTA_COMISSION_PERCENT, repeatGuestSorter } from '../../utils/insightsHelpers'
import { formatNumberAsThousands } from '../../utils/priceHelpers'
import styles from './AnalyzeResults.module.scss'

const AnalyzeResults = () => {
  const currentHotelName = useSelector(selectCurrentHotelName)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getInsights())
  }, [dispatch])

  const oneTimeGuests = useSelector(s => s.insights.oneTimeGuests)
  const repeatGuests = useSelector(s => s.insights.repeatGuests.last3yearsMonthly)
  const otaPerformances = useSelector(s => s.insights.otaPerformances.last3yearsMonthly)
  const directPerformances = useSelector(
    s => s.insights.directPerformances.last3yearsMonthly
  )
  const loading = useSelector(s => s.insights.loading)
  const erred = useSelector(s => s.insights.erred)

  const [otaFilteredYears, setOtaFilteredYears] = useState()

  if (loading) {
    return <LoadingIndicator longWaitingTreshold={120} />
  }

  if (erred) {
    return (
      <div>
        <CustomHeader
          title="Analyze results"
          subtitle={`for ${currentHotelName}`}
          width={1180}
        />
        Data is not ready yet, please try again later.
      </div>
    )
  }

  const totalOTACommission =
    otaPerformances && otaFilteredYears
      ? Object.values(otaPerformances).reduce((prev, group) => {
          return (
            prev +
            Object.keys(group).reduce((prev, curr) => {
              if (otaFilteredYears.includes(`${curr}`)) {
                return prev + group[curr]
              } else {
                return prev
              }
            }, 0)
          )
        }, 0)
      : 0

  return (
    <div className={styles.wrapper}>
      <CustomHeader
        title="Analyze results"
        subtitle={`for ${currentHotelName}`}
        width={940}
      />
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Repeat guests</h2>
        <p className={styles.sectionDesc}>
          Guests who has stayed at your hotel more and 1 time in the past 12 months
        </p>
        <div className={styles.row}>
          <div className={styles.pieHolder}>
            {oneTimeGuests &&
              oneTimeGuests.map(d => (
                <PieCard
                  subtitle={`${d.totalGuests - d.totalRepeatingClients} one-time guests`}
                  data={d.data}
                  key={d.year}
                  title={`One-time guests in ${d.year}`}
                  secondaryLabel={`of ${d.totalGuests} guests`}
                />
              ))}
          </div>
        </div>
        <div className={styles.row}>
          {repeatGuests && (
            <MultiBarCard
              yLabel="guests"
              xLabel="stays"
              data={Object.values(repeatGuests).sort(repeatGuestSorter)}
              title="Repeat guests shown in years"
              barSize={18}
              hasToggles
            />
          )}
        </div>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>OTA booking analysis</h2>
        <div className={styles.row}>
          {otaPerformances && Object.keys(otaPerformances).length > 0 && (
            <ChartBox>
              <div className={styles.chartInfoContainer}>
                <div className={styles.chartInfo}>
                  <div className={styles.chartInfoTitle}>OTA commission paid</div>
                  <div className={styles.chartInfoValue}>
                    ${formatNumberAsThousands(totalOTACommission)}
                  </div>
                  <div className={styles.chartInfoDesc}>
                    Based on average {OTA_COMISSION_PERCENT}% commission
                  </div>
                </div>
                <div className={styles.chartHolder}>
                  <MultiBarCard
                    yLabel="commission ($)"
                    data={Object.values(otaPerformances)}
                    barSize={4}
                    customYLabel
                    noWrapper
                    unit="$"
                    hasToggles
                    height={200}
                    handleChangeFilteredProperties={setOtaFilteredYears}
                  />
                </div>
              </div>
            </ChartBox>
          )}
        </div>
        {directPerformances && (
          <div className={styles.row}>
            <div style={{ width: 600 }}>
              <MultiBarCard
                title="Direct bookings"
                yLabel="Bookings"
                data={Object.values(directPerformances)}
                barSize={5}
                height={250}
                hasLegends
              />
            </div>
            {otaPerformances &&
              Object.keys(otaPerformances).length > 0 &&
              otaFilteredYears && (
                <div className={styles.sliderContainer}>
                  <ComissionSlider totalCommisson={totalOTACommission} />
                </div>
              )}
          </div>
        )}
      </section>
    </div>
  )
}

export default AnalyzeResults
